import type { Shot } from '@/api/studio-production'
import { formatShotSize } from '@/features/studio/shot/storyboard-sheet'

export type ShotCanvasIntent = 'storyboard' | 'video' | 'selection'

const DEFAULT_FORBIDDEN =
  '不要嚎啕大哭，不要夸张五官变形，不要大幅度肢体动作，不要擦眼泪，不要歇斯底里，不新增人物，不改变服装、发色、脸型，不改变站位，不随机增加饰品，不出现动漫脸、CG塑料感、动作穿模、焦点漂移、镜头突然加速、剪辑跳切。'

const DEFAULT_SHOT_DURATION_SEC = 3

function refDefinitionBlock(refImageLabels: string[]): string {
  if (refImageLabels.length === 0) return ''
  const lines = refImageLabels.map((label, i) => {
    if (i === 0) {
      return `@${label}= 主体1唯一角色参考：保持脸型、年龄、发型、服装、体型与身高比例一致。`
    }
    return `@${label}= 场景/风格参考${i}：保持与参考图一致的环境、光影与视觉特征。`
  })
  return `【素材定义】\n${lines.join('\n')}`
}

function shotDurationSec(shot: Shot): number {
  return shot.duration_sec > 0 ? shot.duration_sec : DEFAULT_SHOT_DURATION_SEC
}

function formatTimeRange(startSec: number, endSec: number): string {
  const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1))
  return `${fmt(startSec)}-${fmt(endSec)}秒`
}

function composeShotDesignSegment(shot: Shot, startSec: number, endSec: number): string {
  const parts: string[] = []
  const size = formatShotSize(shot.shot_size)
  const camera = [size, shot.camera_angle?.trim(), shot.camera_movement?.trim()].filter(Boolean).join(' · ')
  if (camera) parts.push(camera)

  const desc = shot.description?.trim() || shot.slug
  parts.push(desc)

  if (shot.storyboard_note?.trim()) {
    parts.push(shot.storyboard_note.trim())
  }

  const soundLines: string[] = []
  if (shot.dialogue?.trim()) soundLines.push(`台词：${shot.dialogue.trim()}`)
  if (shot.sound_note?.trim()) soundLines.push(`音效：${shot.sound_note.trim()}`)
  if (soundLines.length > 0) parts.push(soundLines.join('；'))

  return `${formatTimeRange(startSec, endSec)}：\n${parts.join('。')}`
}

function composeGoalBlock(shots: Shot[], sceneLabel: string, sceneHeading?: string): string {
  const sceneLine = [sceneLabel, sceneHeading?.trim()].filter(Boolean).join(' · ')
  const descriptions = shots.map((s) => s.description?.trim()).filter(Boolean) as string[]

  if (descriptions.length === 0) {
    return `【镜头目标】\n${sceneLine}\n真实电影实拍风格，ARRI Alexa 35，85mm电影镜头，一镜到底，无剪辑。`
  }

  if (descriptions.length === 1) {
    return `【镜头目标】\n${descriptions[0]}\n真实电影实拍风格，ARRI Alexa 35，85mm电影镜头，一镜到底，无剪辑。`
  }

  return `【镜头目标】\n${descriptions[0]}\n${descriptions.slice(1).join('\n')}\n真实电影实拍风格，ARRI Alexa 35，85mm电影镜头，按以下分镜顺序一镜到底，无剪辑。`
}

function composeToneBlock(shots: Shot[]): string | null {
  const musicNotes = shots.map((s) => s.music_note?.trim()).filter(Boolean) as string[]
  if (musicNotes.length === 0) return null
  const unique = [...new Set(musicNotes)]
  return `【色调参数】\n${unique.join('\n')}`
}

function composeStagingBlock(shots: Shot[]): string | null {
  const notes = shots.map((s) => s.storyboard_note?.trim()).filter(Boolean) as string[]
  if (notes.length === 0) {
    const first = shots[0]
    if (!first) return null
    const size = formatShotSize(first.shot_size)
    const camera = [size, first.camera_angle?.trim(), first.camera_movement?.trim()].filter(Boolean)
    if (camera.length === 0) return null
    return `【站位与空间】\n${camera.join(' · ')}`
  }
  return `【站位与空间】\n${notes[0]}`
}

function composeDesignBlock(shots: Shot[]): string {
  let cursor = 0
  const segments = shots.map((shot) => {
    const dur = shotDurationSec(shot)
    const start = cursor
    cursor += dur
    return composeShotDesignSegment(shot, start, cursor)
  })
  return `【镜头设计】\n${segments.join('\n\n')}`
}

/** 勾选批次 → 单个上下文 prompt（素材定义 / 镜头目标 / 色调 / 站位 / 镜头设计 / 禁止项） */
export function composeSelectedShotsCanvasPrompt(input: {
  shots: Shot[]
  sceneLabel: string
  sceneHeading?: string
  refImageLabels?: string[]
}): { title: string; prompt: string } {
  const slugs = input.shots.map((s) => s.slug)
  const title =
    slugs.length <= 2
      ? slugs.join(' + ')
      : `${slugs[0]} … ${slugs[slugs.length - 1]}（${slugs.length}镜）`

  const sections: string[] = []

  const refs = refDefinitionBlock(input.refImageLabels ?? [])
  if (refs) sections.push(refs)

  sections.push(composeGoalBlock(input.shots, input.sceneLabel, input.sceneHeading))

  const tone = composeToneBlock(input.shots)
  if (tone) sections.push(tone)

  const staging = composeStagingBlock(input.shots)
  if (staging) sections.push(staging)

  sections.push(composeDesignBlock(input.shots))
  sections.push(`【禁止项】\n${DEFAULT_FORBIDDEN}`)

  return { title, prompt: sections.join('\n\n') }
}

/** @deprecated 使用 composeSelectedShotsCanvasPrompt */
export function composeVideoBatchCanvasPrompt(input: {
  shots: Shot[]
  sceneLabel: string
  sceneHeading?: string
  refImageLabels?: string[]
}): { title: string; prompt: string } {
  return composeSelectedShotsCanvasPrompt(input)
}

export function shotPublicIdsKey(ids: string[]): string {
  return [...ids].sort().join(',')
}
